/**
 * useCampaignExport Hook
 *
 * React hook for exporting campaign data to platform-specific CSV formats.
 * Handles single and batch exports with progress tracking.
 *
 * Usage:
 *   const { isExporting, results, exportSingle, exportBatch, downloadAll } = useCampaignExport(campaignData);
 *
 *   // Single export
 *   await exportSingle('meta');
 *
 *   // Batch export
 *   await exportBatch(['meta', 'google_ads', 'pinterest']);
 *   await downloadAll(); // Downloads as ZIP
 */

import { useState, useCallback } from 'react';
import {
  exportCampaign,
  exportCampaignBatch,
  downloadAsZip,
  downloadBlob,
  validateForPlatform,
  CampaignData,
  Platform,
  ExportResult,
  ValidationResult,
} from '@/lib/csv-export';

export interface UseExportReturn {
  /** Whether an export is in progress */
  isExporting: boolean;

  /** Export results by platform */
  results: Map<Platform, ExportResult>;

  /** Global errors (not platform-specific) */
  errors: string[];

  /** Export to a single platform (auto-downloads) */
  exportSingle: (platform: Platform, format?: 'csv' | 'xlsx') => Promise<void>;

  /** Export to multiple platforms */
  exportBatch: (platforms: Platform[], format?: 'csv' | 'xlsx') => Promise<void>;

  /** Download a specific platform's result */
  downloadResult: (platform: Platform) => void;

  /** Download all successful results (single file or ZIP) */
  downloadAll: () => Promise<void>;

  /** Clear all results and errors */
  clearResults: () => void;

  /** Validate campaign data for a platform without exporting */
  validate: (platform: Platform) => ValidationResult;

  /** Get summary of export results */
  getSummary: () => ExportSummary;
}

export interface ExportSummary {
  total: number;
  successful: number;
  failed: number;
  warnings: string[];
}

/**
 * Hook for exporting campaign data to platform-specific CSV formats
 *
 * @param campaignData - The campaign data to export
 * @returns Export functions and state
 */
export function useCampaignExport(campaignData: CampaignData): UseExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [results, setResults] = useState<Map<Platform, ExportResult>>(new Map());
  const [errors, setErrors] = useState<string[]>([]);

  /**
   * Export to a single platform
   * Automatically triggers download on success
   */
  const exportSingle = useCallback(
    async (platform: Platform, format: 'csv' | 'xlsx' = 'csv') => {
      setIsExporting(true);
      setErrors([]);

      try {
        const result = await exportCampaign(campaignData, { platform, format });
        setResults(new Map([[platform, result]]));

        if (result.success) {
          // Auto-download
          downloadBlob(result.blob, result.filename);
        } else {
          setErrors(result.errors);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        setErrors([message]);
      } finally {
        setIsExporting(false);
      }
    },
    [campaignData]
  );

  /**
   * Export to multiple platforms
   * Does NOT auto-download - call downloadAll() after
   */
  const exportBatch = useCallback(
    async (platforms: Platform[], format: 'csv' | 'xlsx' = 'csv') => {
      if (platforms.length === 0) {
        setErrors(['No platforms selected']);
        return;
      }

      setIsExporting(true);
      setErrors([]);

      try {
        const batchResults = await exportCampaignBatch(
          campaignData,
          platforms,
          format
        );
        setResults(batchResults);

        // Collect all errors
        const allErrors: string[] = [];
        batchResults.forEach((result, platform) => {
          if (!result.success) {
            const platformName = platform.replace('_', ' ');
            result.errors.forEach((err) => {
              allErrors.push(`${platformName}: ${err}`);
            });
          }
        });
        setErrors(allErrors);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Batch export failed';
        setErrors([message]);
      } finally {
        setIsExporting(false);
      }
    },
    [campaignData]
  );

  /**
   * Download a specific platform's result
   */
  const downloadResult = useCallback(
    (platform: Platform) => {
      const result = results.get(platform);
      if (result?.success && result.blob.size > 0) {
        downloadBlob(result.blob, result.filename);
      }
    },
    [results]
  );

  /**
   * Download all successful results
   * Single file if only one result, ZIP if multiple
   */
  const downloadAll = useCallback(async () => {
    if (results.size === 0) return;

    // Count successful exports
    const successfulResults = Array.from(results.entries()).filter(
      ([, result]) => result.success && result.blob.size > 0
    );

    if (successfulResults.length === 0) return;

    if (successfulResults.length === 1) {
      // Single file - just download it
      const [platform] = successfulResults[0];
      downloadResult(platform);
    } else {
      // Multiple files - create ZIP
      try {
        const zipBlob = await downloadAsZip(results, campaignData.title);
        const sanitizedTitle = campaignData.title
          .replace(/[^a-zA-Z0-9]/g, '-')
          .toLowerCase();
        const date = new Date().toISOString().split('T')[0];
        const filename = `${sanitizedTitle}-campaigns-${date}.zip`;
        downloadBlob(zipBlob, filename);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'ZIP creation failed';
        setErrors((prev) => [...prev, message]);
      }
    }
  }, [results, campaignData.title, downloadResult]);

  /**
   * Clear all results and errors
   */
  const clearResults = useCallback(() => {
    setResults(new Map());
    setErrors([]);
  }, []);

  /**
   * Validate campaign data for a platform without exporting
   */
  const validate = useCallback(
    (platform: Platform): ValidationResult => {
      return validateForPlatform(campaignData, platform);
    },
    [campaignData]
  );

  /**
   * Get summary of export results
   */
  const getSummary = useCallback((): ExportSummary => {
    const total = results.size;
    let successful = 0;
    let failed = 0;
    const warnings: string[] = [];

    results.forEach((result, platform) => {
      if (result.success) {
        successful++;
        result.warnings.forEach((w) => {
          warnings.push(`${platform}: ${w}`);
        });
      } else {
        failed++;
      }
    });

    return { total, successful, failed, warnings };
  }, [results]);

  return {
    isExporting,
    results,
    errors,
    exportSingle,
    exportBatch,
    downloadResult,
    downloadAll,
    clearResults,
    validate,
    getSummary,
  };
}

export default useCampaignExport;
